/**
 * Copyright 2025 Amazon.com, Inc. or its affiliates. All Rights Reserved.
 * SPDX-License-Identifier: MIT-0
 */

import { generateClient } from 'aws-amplify/api';

const client = generateClient();

export const getAnalytics = async () => {
  const query = `
    query GetAnalytics {
      getAdminAnalytics {
        totalTrips
        activeDrivers
        totalUsers
        totalRevenue
        cashRevenue
        nonCashRevenue
        averageRating
        tripsByHour { hour trips }
        revenueByDay { day revenue }
        driverStatus { status count color }
        recentActivity { action time }
      }
    }
  `;
  
  const result: any = await client.graphql({ query });
  return result.data.getAdminAnalytics;
};

export interface DriverRevenueRow {
  driverId: string;
  driverName?: string;
  grossRevenue: number;
  cashRevenue: number;
  nonCashRevenue: number;
  tripCount: number;
}

/**
 * Windowed, paginated driver revenue leaderboard.
 * period: 'WEEK' | 'MONTH' · order: 'TOP' | 'LOW'
 */
export const getDriverRevenueLeaderboard = async (params: {
  period?: 'WEEK' | 'MONTH';
  order?: 'TOP' | 'LOW';
  limit?: number;
  nextToken?: string | null;
}): Promise<{ items: DriverRevenueRow[]; nextToken: string | null; total: number }> => {
  const query = `
    query GetDriverRevenueLeaderboard($period: RevenuePeriod, $order: String, $limit: Int, $nextToken: String) {
      getDriverRevenueLeaderboard(period: $period, order: $order, limit: $limit, nextToken: $nextToken) {
        items {
          driverId
          driverName
          grossRevenue
          cashRevenue
          nonCashRevenue
          tripCount
        }
        nextToken
        total
      }
    }
  `;

  const result: any = await client.graphql({
    query,
    variables: {
      period: params.period ?? 'MONTH',
      order: params.order ?? 'TOP',
      limit: params.limit ?? 10,
      nextToken: params.nextToken ?? null,
    },
  });
  return result.data.getDriverRevenueLeaderboard;
};

/**
 * Coerce a numeric field that may arrive as a plain number, a numeric string,
 * or a wrapped object like { source: "0.0", parsedValue: 0 } into a number.
 */
const toNum = (v: any): number => {
  if (v == null) return 0;
  if (typeof v === 'number') return v;
  if (typeof v === 'object') return Number(v.parsedValue ?? v.source ?? 0) || 0;
  const n = Number(v);
  return Number.isNaN(n) ? 0 : n;
};

export const getTrips = async () => {
  const query = `
    query GetTrips {
      listAllTrips {
        items {
          id
          riderId
          riderName
          driverId
          driverName
          status
          pickup { address latitude longitude }
          destination { address latitude longitude }
          fareEstimate
          finalFare
          distance
          duration
          paymentMethod
          createdAt
          startedAt
          completedAt
          cancelledAt
          cancellationReason
        }
        nextToken
      }
    }
  `;
  
  const result: any = await client.graphql({ query });
  // Normalize numeric fields so downstream UI/format code never sees wrapped objects
  return (result.data.listAllTrips.items ?? []).map((t: any) => ({
    ...t,
    fareEstimate: toNum(t.fareEstimate),
    finalFare: toNum(t.finalFare),
    distance: toNum(t.distance),
    duration: toNum(t.duration),
  }));
};

export const getDrivers = async () => {
  const query = `
    query GetDrivers {
      listAllDrivers {
        items {
          id
          name
          phone
          email
          profilePhotoUrl
          status
          createdAt
        }
        nextToken
      }
    }
  `;
  
  const result: any = await client.graphql({ query });
  return result.data.listAllDrivers.items;
};

export const getUsers = async () => {
  const query = `
    query GetUsers {
      listAllUsers {
        items {
          id
          name
          email
          phone
          userType
          tripCount
          rating
          createdAt
        }
        nextToken
      }
    }
  `;
  
  const result: any = await client.graphql({ query });
  return result.data.listAllUsers.items;
};

export interface ErrorLogFileRef {
  key: string;
  source: 'frontend' | 'backend';
  sizeBytes: number;
  lastModified: string;
  presignedUrl: string;
}

export const listErrorLogFiles = async (params: {
  source?: 'frontend' | 'backend' | 'all';
  dateFrom?: string;
  dateTo?: string;
  limit?: number;
}): Promise<ErrorLogFileRef[]> => {
  const query = `
    query ListErrorLogs($source: String, $dateFrom: String, $dateTo: String, $limit: Int) {
      listErrorLogs(source: $source, dateFrom: $dateFrom, dateTo: $dateTo, limit: $limit) {
        success
        error
        files {
          key
          source
          sizeBytes
          lastModified
          presignedUrl
        }
      }
    }
  `;

  const result: any = await client.graphql({
    query,
    variables: {
      source: params.source ?? 'all',
      dateFrom: params.dateFrom,
      dateTo: params.dateTo,
      limit: params.limit ?? 50,
    },
  });

  const data = result.data.listErrorLogs;
  if (!data.success) throw new Error(data.error ?? 'listErrorLogs failed');
  return data.files;
};

/**
 * Subscribe to a GraphQL subscription.
 * Returns an object with an `unsubscribe()` method.
 */
export function graphqlSubscribe(
  query: string,
  variables: Record<string, any>,
  onData: (data: any) => void
): { unsubscribe: () => void } {
  const sub = (client.graphql({ query, variables }) as any).subscribe({
    next: ({ data }: any) => onData(data),
    error: (err: any) => console.error('[SOS subscription error]', err),
  });
  return { unsubscribe: () => sub.unsubscribe() };
}
